/**
 * French Privacy Policy translations
 */
export const privacy = {
  pageTitle: "Politique de Confidentialité",
  heroSubtitle: "Votre vie privée est importante pour nous. Cette politique explique comment nous collectons, utilisons et protégeons vos informations personnelles.",
  lastUpdated: "Dernière Mise à Jour : Janvier 2026",
  effectiveDate: "Date d'Effet : 1er Janvier 2026",

  intro: {
    title: "Introduction",
    welcome: "Bienvenue à l'Observatoire des Futurs de la Jeunesse des Conseils de Développement de la JCI. Nous nous engageons à protéger votre vie privée et à assurer la transparence sur la façon dont nous traitons vos données.",
    commitment: "Cette Politique de Confidentialité explique quelles informations personnelles nous collectons, comment nous les utilisons et vos droits concernant vos données. Nous suivons les principes de minimisation des données et de limitation des finalités.",
    scope: "Cette politique s'applique à tous les utilisateurs de la plateforme de l'Observatoire des Futurs de la Jeunesse, y compris les membres de la JCI (Junior Chamber International) et les visiteurs publics.",
  },

  controller: {
    title: "Responsable du Traitement",
    description: "Le responsable du traitement de vos informations personnelles est :",
    name: "Conseils de Développement de la JCI",
    contact: "Pour les demandes relatives à la vie privée, veuillez contacter :",
  },

  collection: {
    title: "Informations que Nous Collectons",
    description: "Nous collectons différents types d'informations selon la façon dont vous interagissez avec notre plateforme :",
    account: {
      title: "Informations de Compte",
      email: "Adresse e-mail (pour l'authentification et les notifications)",
      name: "Nom d'affichage (optionnel, pour la personnalisation)",
      avatar: "Photo de profil (optionnel, stockée en sécurité)",
      organization: "Affiliation à l'organisation JCI (optionnel)",
    },
    preferences: {
      title: "Préférences Utilisateur",
      language: "Paramètre de langue préférée",
      theme: "Préférence de mode clair/sombre",
      country: "Pays de focus pour l'analyse des données",
      interest: "Domaine d'intérêt principal (ex : Santé Mentale, Entrepreneuriat)",
    },
    analytics: {
      title: "Données d'Analyse (avec consentement)",
      description: "Lorsque vous consentez aux analyses, nous collectons :",
      pages: "Pages visitées et modèles de navigation",
      scroll: "Profondeur de défilement sur les pages de contenu",
      time: "Temps passé sur la plateforme",
      device: "Type de navigateur et d'appareil (anonymisé)",
    },
  },

  usage: {
    title: "Comment Nous Utilisons Vos Données",
    description: "Nous utilisons vos informations aux fins suivantes :",
    purpose1: {
      title: "Fourniture de Services",
      description: "Pour vous authentifier, personnaliser votre expérience et fournir les fonctionnalités de l'Observatoire que vous demandez.",
    },
    purpose2: {
      title: "Amélioration de la Plateforme",
      description: "Pour comprendre comment les utilisateurs interagissent avec notre plateforme et améliorer les fonctionnalités (uniquement avec consentement analytique).",
    },
    purpose3: {
      title: "Communications",
      description: "Pour vous envoyer des mises à jour importantes sur la plateforme, si vous avez opté pour les notifications.",
    },
    purpose4: {
      title: "Sécurité",
      description: "Pour protéger contre les accès non autorisés et maintenir l'intégrité de nos systèmes.",
    },
  },

  legal: {
    title: "Base Légale du Traitement",
    description: "En vertu du RGPD, nous traitons vos données sur les bases légales suivantes :",
    consent: {
      title: "Consentement (Article 6(1)(a))",
      description: "Pour le suivi analytique et les communications marketing. Vous pouvez retirer votre consentement à tout moment.",
    },
    contract: {
      title: "Exécution du Contrat (Article 6(1)(b))",
      description: "Pour fournir les services que vous avez demandés lors de la création d'un compte.",
    },
    legitimate: {
      title: "Intérêts Légitimes (Article 6(1)(f))",
      description: "Pour les mesures de sécurité et la prévention de la fraude, équilibrés avec vos droits.",
    },
    obligation: {
      title: "Obligation Légale (Article 6(1)(c))",
      description: "Pour se conformer aux lois et réglementations applicables.",
    },
  },

  cookies: {
    title: "Cookies et Stockage Local",
    description: "Nous utilisons des cookies et le stockage du navigateur pour améliorer votre expérience :",
    essential: {
      title: "Cookies Essentiels",
      description: "Nécessaires pour l'authentification et les fonctionnalités de base. Ne peuvent pas être désactivés.",
    },
    analytics: {
      title: "Cookies d'Analyse (Google Analytics 4)",
      description: "Nous aident à comprendre comment les visiteurs utilisent notre site. Actifs uniquement avec votre consentement.",
    },
    preferences: {
      title: "Stockage des Préférences (localStorage)",
      description: "Stocke vos choix de langue, thème et consentement localement dans votre navigateur.",
    },
    manage: "Vous pouvez gérer vos préférences de cookies à tout moment dans",
    manageLink: "Paramètres",
  },

  thirdParty: {
    title: "Services Tiers",
    description: "Nous utilisons les services tiers suivants pour exploiter notre plateforme :",
    ga4: "Analyse de site web et suivi d'utilisation (avec consentement)",
    lovable: "Infrastructure backend, authentification et base de données",
    osm: "Cartes interactives montrant la présence mondiale de la JCI",
    fonts: "Livraison de typographie (famille de polices Plus Jakarta Sans)",
  },

  retention: {
    title: "Conservation des Données",
    description: "Nous conservons vos données uniquement le temps nécessaire aux fins décrites :",
    dataType: "Type de Données",
    period: "Période de Conservation",
    account: "Données de Compte",
    accountPeriod: "Jusqu'à suppression du compte ou 2 ans d'inactivité",
    analytics: "Données d'Analyse",
    analyticsPeriod: "14 mois (par défaut Google Analytics)",
    logs: "Journaux d'Erreurs",
    logsPeriod: "90 jours",
  },

  rights: {
    title: "Vos Droits à la Vie Privée (RGPD)",
    description: "En vertu du Règlement Général sur la Protection des Données, vous avez les droits suivants :",
    access: {
      title: "Droit d'Accès",
      description: "Demander une copie de toutes les données personnelles que nous détenons sur vous.",
    },
    rectification: {
      title: "Droit de Rectification",
      description: "Demander la correction de données personnelles inexactes ou incomplètes.",
    },
    erasure: {
      title: "Droit à l'Effacement",
      description: "Demander la suppression de vos données personnelles ('droit à l'oubli').",
    },
    restriction: {
      title: "Droit à la Limitation",
      description: "Demander la limitation du traitement dans certaines circonstances.",
    },
    portability: {
      title: "Droit à la Portabilité des Données",
      description: "Recevoir vos données dans un format structuré et lisible par machine.",
    },
    object: {
      title: "Droit d'Opposition",
      description: "S'opposer au traitement basé sur les intérêts légitimes ou pour le marketing direct.",
    },
    exercise: "Pour exercer ces droits, visitez votre",
    settingsLink: "page Paramètres",
  },

  international: {
    title: "Conformité Internationale en Matière de Confidentialité",
    description: "En tant qu'organisation mondiale opérant dans plus de 100 pays, nous nous engageons à respecter les lois et réglementations locales en matière de confidentialité partout où se trouvent nos membres.",
    commitment: "Bien que nous utilisions le RGPD comme norme de base (car il offre les protections les plus complètes), nous respectons également les lois locales applicables en matière de confidentialité, notamment :",
    ukGdpr: "UK GDPR pour les résidents du Royaume-Uni",
    lgpd: "LGPD (Loi Générale sur la Protection des Données) pour les résidents brésiliens",
    pipeda: "PIPEDA pour les résidents canadiens",
    appi: "APPI (Loi sur la Protection des Informations Personnelles) pour les résidents japonais",
    localRights: "Si votre pays dispose d'une législation spécifique en matière de confidentialité, vous pouvez avoir des droits supplémentaires. Contactez-nous pour comprendre comment vos lois locales s'appliquent.",
  },

  children: {
    title: "Vie Privée des Enfants",
    description: "L'Observatoire des Futurs de la Jeunesse est conçu pour les utilisateurs de 18 ans et plus, conformément aux exigences d'adhésion de la JCI. Nous ne collectons pas sciemment de données sur les enfants de moins de 16 ans.",
    action: "Si vous pensez que nous avons collecté par inadvertance des données d'un mineur, veuillez nous contacter immédiatement à privacy@jci.cc.",
  },

  security: {
    title: "Sécurité des Données",
    description: "Nous mettons en œuvre des mesures de sécurité robustes pour protéger vos données :",
    encryption: "Toutes les données sont chiffrées en transit (HTTPS/TLS) et au repos",
    access: "Des contrôles d'accès stricts limitent qui peut voir vos informations",
    rls: "La Sécurité au Niveau des Lignes (RLS) garantit que vous ne pouvez accéder qu'à vos propres données",
    audit: "Audits de sécurité réguliers et évaluations des vulnérabilités",
  },

  transfers: {
    title: "Transferts Internationaux de Données",
    description: "Vos données peuvent être traitées dans des pays en dehors de votre résidence, y compris les États-Unis et l'Union Européenne.",
    safeguards: "Nous veillons à ce que des garanties appropriées soient en place, y compris les Clauses Contractuelles Types (CCT) lorsque la loi l'exige.",
  },

  changes: {
    title: "Modifications de cette Politique",
    description: "Nous pouvons mettre à jour cette Politique de Confidentialité périodiquement pour refléter les changements dans nos pratiques ou les exigences légales.",
    notification: "Les changements significatifs seront communiqués par e-mail (si vous avez un compte) ou par un avis visible sur notre plateforme. La date de 'Dernière Mise à Jour' en haut indique quand les modifications ont été effectuées.",
  },

  contact: {
    title: "Nous Contacter",
    description: "Si vous avez des questions sur cette Politique de Confidentialité ou souhaitez exercer vos droits, veuillez nous contacter :",
    email: "Demandes de Confidentialité :",
    dpa: "Vous avez également le droit de déposer une plainte auprès de votre autorité locale de protection des données si vous pensez que vos droits ont été violés.",
  },
} as const;
